/*
 * Country / store selector.
 * <country-selector>         list + search (sections/country-selector.liquid)
 * <country-selector-drawer>  drawer shell that lazy-loads the section (snippets/country-selector-drawer.liquid)
 * <store-selector>           full page: country + language + man/woman collection (sections/main-store-selector.liquid)
 *
 * Countries of this store are switched with the localization form (same URL, new currency).
 * Countries of other Scalpers stores link to that store's home (assets/country-selector-stores.json).
 */
(function () {
  if (customElements.get('country-selector')) return;

  const SUGGESTION_KEY = 'cs-detected-country';

  // "€ EUR", "$ USD"... The symbol comes from Shopify for this store's countries, from Intl for the rest.
  // Only Latin letters and the common currency signs are shown; other scripts (؋, د.ج, ֏) fall back to the code alone.
  const READABLE_SYMBOL = /^[A-Za-złč$€£¥₩₹₺₪₱฿₫₦₴₽₸₡.\/\s]+$/;
  const currencySymbols = {};
  function formatCurrency(code, symbol) {
    if (!code) return '';
    if (!symbol) {
      if (!(code in currencySymbols)) {
        try {
          const part = new Intl.NumberFormat('en', { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' })
            .formatToParts(0)
            .find((p) => p.type === 'currency');
          currencySymbols[code] = part ? part.value : '';
        } catch (e) {
          currencySymbols[code] = '';
        }
      }
      symbol = currencySymbols[code];
    }
    return symbol && symbol !== code && READABLE_SYMBOL.test(symbol) ? `${symbol} ${code}` : code;
  }

  // Shopify serializes some country names HTML-escaped ("Antigua &amp; Barbuda").
  const decoder = document.createElement('textarea');
  function decodeEntities(value) {
    decoder.innerHTML = String(value || '');
    return decoder.value;
  }

  // "[country]"-style placeholders. A function replacement, so "$&" or "$'" in a value are never expanded.
  const fill = (template, token, value) => String(template || '').split(token).join(value);

  // Sets a field of the localization form. The Liquid form tag already adds return_to; a second one would be sent too.
  function setFormField(form, name, value) {
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  // Focus back to the element that opened the drawer; if it's gone or hidden (the geolocation bar slides up),
  // to the visible footer trigger.
  function focusOpener(opener) {
    const usable = (element) => element && element.isConnected && element.focus && element.getClientRects().length > 0;
    const target = usable(opener)
      ? opener
      : Array.from(document.querySelectorAll('[data-country-selector-trigger]')).find(usable);
    if (target) target.focus({ preventScroll: true });
  }

  // Back from the browser cache after submitting: the page comes back with the "submitting" state still on.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) document.querySelectorAll('.cs.is-submitting, store-selector.is-submitting').forEach((el) => el.classList.remove('is-submitting'));
  });

  let storesPromise = null;
  function loadStores(url) {
    if (!storesPromise) {
      storesPromise = fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        // The directory is hand-edited: allow whole-line "//" comments.
        .then((text) => JSON.parse(text.replace(/^\s*\/\/.*$/gm, '')))
        .catch((error) => {
          storesPromise = null;
          throw error;
        });
    }
    return storesPromise;
  }

  let suggestionPromise = null;
  function loadDetectedCountry(url) {
    if (suggestionPromise) return suggestionPromise;
    let cached = null;
    try {
      cached = sessionStorage.getItem(SUGGESTION_KEY);
    } catch (e) {}
    if (cached !== null) {
      suggestionPromise = Promise.resolve(cached || null);
      return suggestionPromise;
    }
    suggestionPromise = fetch(url)
      .then((response) => response.json())
      .then((data) => {
        const iso = (data && data.detected_values && data.detected_values.country && data.detected_values.country.handle) || '';
        try {
          sessionStorage.setItem(SUGGESTION_KEY, iso);
        } catch (e) {}
        return iso || null;
      })
      .catch(() => null);
    return suggestionPromise;
  }

  const normalize = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .trim();

  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  function displayNames(locale, type) {
    try {
      return new Intl.DisplayNames([locale, 'en'], { type });
    } catch (e) {
      return null;
    }
  }

  const languageNames = {};
  function languageName(code) {
    if (!languageNames[code]) {
      const names = displayNames(code, 'language');
      const name = (names && names.of(code)) || code.toUpperCase();
      languageNames[code] = name.charAt(0).toLocaleUpperCase(code) + name.slice(1);
    }
    return languageNames[code];
  }

  const DEFAULT_COLLECTIONS = { man: 'pages/man', woman: 'pages/woman' };
  const stripWww = (domain) => String(domain || '').replace(/^www\./, '');

  // The directory is shared by every Scalpers store. Each country belongs to the first destination
  // that lists it; destinations on this shop's domain are skipped because localization already
  // provides them live (with this store's real markets and currencies).
  // Entries: { iso, name, currency, symbol, search, own: true, languages: [{ code, label }] }
  //       or { iso, name, currency, symbol, search, own: false, host, links: [{ code, label, url, collections }] }
  function buildDirectory({ ownCountries, directory, shopDomain, locale }) {
    const destinations = (directory && directory.destinations) || [];
    const aliases = (directory && directory.aliases) || {};
    shopDomain = stripWww(shopDomain || window.location.hostname);
    const owner = {};
    destinations.forEach((destination) => {
      Object.keys(destination.countries || {}).forEach((iso) => {
        if (!owner[iso]) owner[iso] = destination;
      });
    });
    const isExternal = (iso) => owner[iso] && stripWww(owner[iso].domain) !== shopDomain;
    const ownDestination = destinations.find((destination) => stripWww(destination.domain) === shopDomain);
    const regionNames = displayNames(locale, 'region');
    const englishNames = displayNames('en', 'region');

    const entries = [];
    (ownCountries || []).forEach((country) => {
      if (isExternal(country.iso)) return;
      entries.push({
        iso: country.iso,
        name: decodeEntities(country.name),
        currency: country.currency,
        symbol: decodeEntities(country.symbol),
        own: true,
        languages: (country.languages || []).map((language) => ({ code: language.code, label: language.name || languageName(language.code) })),
        links: null,
      });
    });
    Object.keys(owner).forEach((iso) => {
      if (!isExternal(iso)) return;
      const destination = owner[iso];
      const collections = destination.collections || DEFAULT_COLLECTIONS;
      const withCountry = (url) => {
        if (destination.countryParam) url.searchParams.set('country', iso);
        return url.toString();
      };
      const links = Object.keys(destination.languages || {}).map((code) => {
        const base = destination.languages[code];
        const collectionUrls = {};
        Object.keys(collections).forEach((key) => (collectionUrls[key] = withCountry(new URL(collections[key], base))));
        return { code, label: languageName(code), url: withCountry(new URL(base)), collections: collectionUrls };
      });
      if (!links.length) return;
      entries.push({
        iso,
        name: (regionNames && regionNames.of(iso)) || iso,
        currency: destination.countries[iso] || '',
        symbol: '',
        own: false,
        links,
        host: stripWww(destination.domain),
      });
    });

    entries.forEach((entry) => {
      entry.search = normalize(
        [entry.name, entry.iso, englishNames && englishNames.of(entry.iso)].concat(aliases[entry.iso] || []).join(' | ')
      );
    });
    entries.sort((a, b) => a.name.localeCompare(b.name, locale));

    const byIso = {};
    entries.forEach((entry) => (byIso[entry.iso] = entry));
    return { entries, byIso, ownCollections: (ownDestination && ownDestination.collections) || DEFAULT_COLLECTIONS };
  }

  // A malformed directory (e.g. a broken URL makes `new URL` throw) falls back to this store's own countries.
  function safeBuildDirectory(options) {
    try {
      return buildDirectory(options);
    } catch (error) {
      console.warn('[country-selector] Invalid stores directory, showing only this store', error);
      return buildDirectory({ ...options, directory: {} });
    }
  }

  // Results first that start with the query, then the ones that contain it.
  function searchEntries(entries, query) {
    const matches = entries.filter((entry) => entry.search.includes(query));
    matches.sort((a, b) => (normalize(a.name).startsWith(query) ? 0 : 1) - (normalize(b.name).startsWith(query) ? 0 : 1));
    return matches;
  }

  class CountrySelector extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.current = this.dataset.current;
      this.locale = document.documentElement.lang || 'en';
      this.results = this.querySelector('[data-cs-results]');
      this.status = this.querySelector('[data-cs-status]');
      this.searchWrapper = this.querySelector('.cs__search');
      this.searchInput = this.querySelector('.cs__search-input');
      this.form = this.querySelector('[data-cs-form]');
      this.strings = this.readJson('[data-cs-strings]') || {};
      // Translations come HTML-escaped from the Liquid `t` filter and are escaped again when rendered.
      Object.keys(this.strings).forEach((key) => (this.strings[key] = decodeEntities(this.strings[key])));
      this.ownCountries = this.readJson('[data-cs-countries]') || [];
      this.suggestedRow = this.querySelector('[data-cs-suggested-row]');
      this.suggestedSlot = this.querySelector('[data-cs-suggested]');
      this.suggested = null;

      this.querySelectorAll('[data-cs-currency]').forEach((node) => {
        node.textContent = formatCurrency(node.dataset.csCurrency, decodeEntities(node.dataset.csSymbol));
      });

      this.results.addEventListener('click', this.onResultsClick.bind(this));
      if (this.suggestedSlot) this.suggestedSlot.addEventListener('click', this.onResultsClick.bind(this));
      this.results.addEventListener('keydown', this.onResultsKeydown.bind(this));
      this.searchInput.addEventListener('input', () => this.render());
      this.searchInput.addEventListener('keydown', this.onSearchKeydown.bind(this));

      this.ready = loadStores(this.dataset.storesUrl)
        .catch((error) => {
          console.warn('[country-selector] Could not load the stores directory', error);
          return {};
        })
        .then((stores) => {
          this.buildEntries(stores);
          this.searchWrapper.hidden = false;
          this.render();
        });

      loadDetectedCountry(this.dataset.suggestionsUrl).then((iso) => {
        if (iso && !this.suggested) this.setSuggested(iso);
      });
    }

    readJson(selector) {
      const node = this.querySelector(selector);
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        return null;
      }
    }

    buildEntries(directory) {
      const { entries, byIso } = safeBuildDirectory({
        ownCountries: this.ownCountries,
        directory,
        shopDomain: this.dataset.shopDomain,
        locale: this.locale,
      });
      this.entries = entries;
      this.byIso = byIso;
    }

    setSuggested(iso) {
      iso = String(iso || '').toUpperCase();
      if (!iso || iso === this.current) return;
      this.suggested = iso;
      if (this.entries) this.renderSuggested();
    }

    // "Suggested for you" row of the info block, filled with the detected country when it differs from the current one.
    renderSuggested() {
      if (!this.suggestedRow) return;
      const entry = this.suggested && this.byIso[this.suggested];
      this.suggestedRow.hidden = !entry;
      this.suggestedSlot.innerHTML = entry ? this.renderEntry(entry, 'suggested', 'div') : '';
    }

    render() {
      if (!this.entries) return;
      const query = normalize(this.searchInput.value);
      let html = '';
      let count;

      if (query) {
        const matches = searchEntries(this.entries, query);
        count = matches.length;
        html = matches.length
          ? this.renderGroup(this.strings.results, matches, 'results')
          : `<p class="cs__empty">${escapeHtml(fill(this.strings.noResults, '[query]', this.searchInput.value))}</p>`;
      } else {
        count = this.entries.length;
        html += this.renderGroup(this.strings.all, this.entries, 'all');
      }

      this.results.innerHTML = html;
      this.renderSuggested();
      if (this.status) this.status.textContent = fill(this.strings.count, '[count]', count);
    }

    renderGroup(title, entries, id) {
      return `<div class="cs__group">
        <p class="cs__group-title" id="cs-group-${id}">${escapeHtml(title || '')}</p>
        <ul class="cs__list" role="list" aria-labelledby="cs-group-${id}">
          ${entries.map((entry) => this.renderEntry(entry, id)).join('')}
        </ul>
      </div>`;
    }

    // A country row: name on the left, currency on the right. This store's countries are buttons (localization
    // form), other stores' are links, or a toggle with the language links when the store has several.
    renderEntry(entry, groupId, wrapper = 'li') {
      const isCurrent = entry.iso === this.current && !entry.links;
      const currency = escapeHtml(formatCurrency(entry.currency, entry.symbol));
      const body = `<span class="cs__name">${escapeHtml(entry.name)}</span><span class="cs__meta">${currency}</span>`;
      const tag = (inner) => `<${wrapper}>${inner}</${wrapper}>`;

      if (!entry.links) {
        return tag(`<button type="button" class="cs__option" data-cs-country="${entry.iso}"${isCurrent ? ' aria-current="true"' : ''}>${body}</button>`);
      }
      if (entry.links.length === 1) {
        return tag(`<a class="cs__option" href="${escapeHtml(entry.links[0].url)}" hreflang="${entry.links[0].code}" data-cs-iso="${entry.iso}">${body}</a>`);
      }
      const panelId = `cs-langs-${groupId}-${entry.iso}`;
      return `<${wrapper}>
        <button type="button" class="cs__option" data-cs-toggle aria-expanded="false" aria-controls="${panelId}">${body}</button>
        <div class="cs__langs" id="${panelId}" hidden>
          <span class="cs__langs-title">${escapeHtml(this.strings.chooseLanguage || '')}</span>
          ${entry.links
            .map(
              (link) =>
                `<a class="cs__lang" href="${escapeHtml(link.url)}" hreflang="${link.code}" lang="${link.code}" data-cs-iso="${entry.iso}" data-cs-language="${escapeHtml(link.label)}">${escapeHtml(link.label)}</a>`
            )
            .join('')}
        </div>
      </${wrapper}>`;
    }

    onResultsClick(event) {
      const toggle = event.target.closest('[data-cs-toggle]');
      if (toggle) {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        this.querySelectorAll('[data-cs-toggle][aria-expanded="true"]').forEach((other) => {
          other.setAttribute('aria-expanded', 'false');
          document.getElementById(other.getAttribute('aria-controls')).hidden = true;
        });
        toggle.setAttribute('aria-expanded', String(!expanded));
        const panel = document.getElementById(toggle.getAttribute('aria-controls'));
        panel.hidden = expanded;
        if (!expanded) {
          const firstLanguage = panel.querySelector('a');
          if (firstLanguage) firstLanguage.focus();
        }
        return;
      }

      // Another store (or one of its languages): a real link, confirmed before leaving.
      const link = event.target.closest('a[data-cs-iso]');
      if (link) {
        const entry = this.byIso[link.dataset.csIso];
        if (!entry) return;
        event.preventDefault();
        this.requestChange({ entry, url: link.href, language: link.dataset.csLanguage || '' });
        return;
      }

      const option = event.target.closest('[data-cs-country]');
      if (!option) return;
      const iso = option.dataset.csCountry;
      if (iso === this.current) {
        this.dispatchEvent(new CustomEvent('country-selector:close', { bubbles: true }));
        return;
      }
      if (this.byIso[iso]) this.requestChange({ entry: this.byIso[iso], url: '', language: '' });
    }

    // Asks the drawer to confirm the change (event "country-selector:confirm", cancelled by whoever handles it).
    // Used outside a drawer, nobody handles it and the change happens straight away.
    requestChange(change) {
      const handled = !this.dispatchEvent(
        new CustomEvent('country-selector:confirm', {
          bubbles: true,
          cancelable: true,
          detail: {
            ...change,
            currency: formatCurrency(change.entry.currency, change.entry.symbol),
            apply: () => this.applyChange(change),
          },
        })
      );
      if (!handled) this.applyChange(change);
    }

    applyChange({ entry, url }) {
      if (url) window.location.href = url;
      else this.submitCountry(entry.iso);
    }

    submitCountry(iso) {
      setFormField(this.form, 'country_code', iso);
      setFormField(this.form, 'return_to', this.dataset.returnTo || window.location.pathname + window.location.search);
      this.classList.add('is-submitting');
      this.form.submit();
    }

    focusableOptions() {
      return Array.from(this.results.querySelectorAll('.cs__option, .cs__langs:not([hidden]) .cs__lang'));
    }

    onSearchKeydown(event) {
      if (event.key === 'ArrowDown') {
        const first = this.focusableOptions()[0];
        if (first) {
          event.preventDefault();
          first.focus();
        }
      } else if (event.key === 'Enter') {
        if (event.isComposing || event.repeat) return;
        const first = this.focusableOptions()[0];
        if (first && this.searchInput.value) {
          event.preventDefault();
          first.click();
        }
      }
    }

    onResultsKeydown(event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const options = this.focusableOptions();
      const index = options.indexOf(document.activeElement);
      if (index === -1) return;
      event.preventDefault();
      const next = options[index + (event.key === 'ArrowDown' ? 1 : -1)];
      if (next) next.focus();
      else if (event.key === 'ArrowUp') this.searchInput.focus();
    }

    focusSearch() {
      if (!this.searchWrapper.hidden) this.searchInput.focus();
    }

    // Each time the drawer opens: empty search, full list, language panels folded.
    reset() {
      this.searchInput.value = '';
      this.classList.remove('is-submitting');
      this.render();
      this.results.scrollTop = 0;
    }
  }
  customElements.define('country-selector', CountrySelector);

  class CountrySelectorDrawer extends HTMLElement {
    connectedCallback() {
      // Rendered inside the footer section; move it to <body> so no ancestor stacking context clips it.
      if (this.parentElement !== document.body) {
        // Theme editor re-renders the footer: drop the drawer moved out by the previous render.
        document.querySelectorAll('body > country-selector-drawer').forEach((previous) => previous.remove());
        document.body.appendChild(this);
        return;
      }
      if (this.bound) return;
      this.bound = true;

      this.panel = this.querySelector('.cs-drawer__panel');
      this.content = this.querySelector('[data-cs-drawer-content]');

      this.querySelectorAll('[data-cs-drawer-close]').forEach((button) => button.addEventListener('click', () => this.close()));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          const input = event.target.closest('.cs__search-input');
          if (input && input.value) {
            event.preventDefault();
            input.value = '';
            input.dispatchEvent(new Event('input'));
            return;
          }
          this.close();
        } else if (event.key === 'Tab') this.trapTab(event);
      });
      this.addEventListener('country-selector:close', () => this.close());
      // A country was picked: close the drawer and ask for confirmation in the pop-up.
      this.addEventListener('country-selector:confirm', (event) => {
        const confirm = document.querySelector('country-selector-confirm');
        if (!confirm || typeof confirm.open !== 'function' || !confirm.isSupported()) return;
        event.preventDefault();
        const opener = this.opener;
        this.close(false);
        confirm.open(event.detail, opener);
      });

      document.querySelectorAll(`[aria-controls="${this.id}"][data-country-selector-trigger]`).forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(trigger);
        });
        // Warm up the request on intent so the list is usually ready when the drawer opens.
        trigger.addEventListener('pointerenter', () => this.load(), { once: true });
        trigger.addEventListener('focus', () => this.load(), { once: true });
      });
    }

    load() {
      if (this.loading) return this.loading;
      const url = `${this.dataset.sectionUrl}${this.dataset.sectionUrl.includes('?') ? '&' : '?'}section_id=country-selector`;
      this.loading = fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((html) => {
          const selector = new DOMParser().parseFromString(html, 'text/html').querySelector('country-selector');
          if (!selector) throw new Error('country-selector not found');
          this.content.replaceChildren(document.importNode(selector, true));
          this.selector = this.content.querySelector('country-selector');
          return this.selector;
        })
        .catch((error) => {
          this.loading = null;
          this.content.innerHTML = `<div class="cs__error"><p>${escapeHtml(this.dataset.errorText || '')}</p>
            <button type="button" class="cs__retry">${escapeHtml(this.dataset.retryText || '')}</button></div>`;
          this.content.querySelector('.cs__retry').addEventListener('click', () => this.open(this.opener, this.lastOptions));
          this.panel.focus();
          throw error;
        });
      return this.loading;
    }

    open(opener, options = {}) {
      this.opener = opener || document.activeElement;
      this.lastOptions = options;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('cs-lock');
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'true');

      const canHover = window.matchMedia('(hover: hover)').matches;
      // Reopening: start from an empty search (synchronously, so nothing typed after opening is wiped).
      if (this.selector && this.selector.entries) this.selector.reset();
      this.panel.focus();

      this.load()
        .then((selector) => {
          if (options.suggest) selector.setSuggested(options.suggest);
          return selector.ready;
        })
        .then(() => {
          if (canHover && this.classList.contains('is-open')) this.selector.focusSearch();
        })
        .catch(() => {});
    }

    // restoreFocus = false when the confirmation pop-up takes over (it returns the focus itself on cancel).
    close(restoreFocus = true) {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('cs-lock');
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'false');
      if (restoreFocus) focusOpener(this.opener);
    }

    // The list re-renders while searching, so focusable elements are resolved on every Tab.
    trapTab(event) {
      const focusables = Array.from(
        this.panel.querySelectorAll('button, [href], input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.disabled && element.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    disconnectedCallback() {
      if (this.classList.contains('is-open') || (this.dialog && this.dialog.open)) document.documentElement.classList.remove('cs-lock');
    }
  }
  customElements.define('country-selector-drawer', CountrySelectorDrawer);

  // Confirmation pop-up (snippets/country-selector-confirm.liquid), a native <dialog>: it already provides the top
  // layer, the focus trap and Escape. Only the "confirm" button applies the change.
  class CountrySelectorConfirm extends HTMLElement {
    connectedCallback() {
      // Same as the drawer: rendered in the footer, lives in <body>.
      if (this.parentElement !== document.body) {
        document.querySelectorAll('body > country-selector-confirm').forEach((previous) => previous.remove());
        document.body.appendChild(this);
        return;
      }
      if (this.bound) return;
      this.bound = true;

      this.dialog = this.querySelector('dialog');
      this.text = this.querySelector('[data-cs-confirm-text]');
      this.accept = this.querySelector('[data-cs-confirm-accept]');
      this.cancel = this.querySelector('[data-cs-confirm-cancel]');
      try {
        this.strings = JSON.parse(this.querySelector('[data-cs-confirm-strings]').textContent);
      } catch (e) {
        this.strings = {};
      }
      // The Liquid `t` filter HTML-escapes the translations ("You&#39;re"); they are escaped again when rendered.
      Object.keys(this.strings).forEach((key) => (this.strings[key] = decodeEntities(this.strings[key])));
      this.dialog.addEventListener('close', () => this.onClose());
      // The form fills the dialog, so a click whose target is the dialog itself is a click on the backdrop.
      this.dialog.addEventListener('pointerdown', (event) => (this.pressedOnBackdrop = event.target === this.dialog));
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog && this.pressedOnBackdrop) this.dialog.close('cancel');
        this.pressedOnBackdrop = false;
      });
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.repeat) event.preventDefault();
      });
    }

    isSupported() {
      return Boolean(this.dialog && typeof this.dialog.showModal === 'function');
    }

    open(detail, returnFocus) {
      this.pending = { detail, returnFocus };
      // "You're currently browsing the [current] online store…" / "Yes, switch to [country]" / "No, stay in [current]"
      const current = decodeEntities(this.dataset.currentName);
      const picked = detail.entry.name;
      this.text.innerHTML = fill(escapeHtml(this.strings.text), '[current]', `<strong>${escapeHtml(current)}</strong>`);
      this.accept.textContent = fill(this.strings.accept, '[country]', picked);
      this.cancel.textContent = fill(this.strings.cancel, '[current]', current);

      this.dialog.returnValue = '';
      document.documentElement.classList.add('cs-lock');
      this.dialog.showModal();
      this.accept.focus();
    }

    onClose() {
      document.documentElement.classList.remove('cs-lock');
      const pending = this.pending;
      this.pending = null;
      if (!pending) return;
      if (this.dialog.returnValue === 'confirm') {
        pending.detail.apply();
      } else {
        focusOpener(pending.returnFocus);
      }
    }

    disconnectedCallback() {
      if (this.classList.contains('is-open') || (this.dialog && this.dialog.open)) document.documentElement.classList.remove('cs-lock');
    }
  }
  customElements.define('country-selector-confirm', CountrySelectorConfirm);

  /*
   * Geolocation pop-up (sections/geo-notification.liquid). Opens when Shopify detects a country different from the
   * current one and the visitor hasn't answered it from that same country in the last 30 days.
   * "stay" / close / Escape / backdrop: remember the answer. "change": remember it and go to the store selector page
   * (data-store-selector-url), which preselects the detected country.
   * The cookie stores the detected country the visitor answered from (cs-geo-answer=ES): browsing later from another
   * country is a new situation, so the pop-up shows again. It is not the old bar's "dismiss-geo", so answers given to
   * the old bar don't hide the new pop-up.
   */
  const GEO_COOKIE = 'cs-geo-answer';
  const GEO_COOKIE_DAYS = 30;
  const GEO_OPEN_DELAY = 800;

  class GeoPopup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

      this.dialog.addEventListener('close', () => this.onClose());
      this.dialog.addEventListener('pointerdown', (event) => (this.pressedOnBackdrop = event.target === this.dialog));
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog && this.pressedOnBackdrop) this.dialog.close('dismiss');
        this.pressedOnBackdrop = false;
      });

      loadDetectedCountry(this.dataset.suggestionsUrl).then((detected) => {
        const iso = String(detected || '').toUpperCase();
        if (!iso || iso === this.dataset.current || this.answeredFrom() === iso) return;
        this.detected = iso;
        setTimeout(() => this.open(), GEO_OPEN_DELAY);
      });
    }

    // Country the visitor last answered the pop-up from, or null.
    answeredFrom() {
      const cookie = document.cookie.split('; ').find((entry) => entry.startsWith(`${GEO_COOKIE}=`));
      return cookie ? decodeURIComponent(cookie.slice(GEO_COOKIE.length + 1)) : null;
    }

    remember() {
      if (!this.detected) return;
      document.cookie = `${GEO_COOKIE}=${encodeURIComponent(this.detected)}; max-age=${GEO_COOKIE_DAYS * 86400}; path=/; SameSite=Lax`;
    }

    open() {
      // Never on top of another dialog (e.g. the country confirmation) or an open drawer.
      if (document.querySelector('dialog[open]') || document.querySelector('country-selector-drawer.is-open')) return;
      this.dialog.returnValue = '';
      document.documentElement.classList.add('cs-lock');
      this.dialog.showModal();
      // It opens on its own (no click), so focus the dialog itself: no focus ring on a button, Tab reaches them.
      this.dialog.focus();
    }

    onClose() {
      document.documentElement.classList.remove('cs-lock');
      this.remember();
      // "Change location": the store selector page, which preselects the detected country.
      if (this.dialog.returnValue === 'change' && this.dataset.storeSelectorUrl) window.location.href = this.dataset.storeSelectorUrl;
    }
  }
  customElements.define('geo-popup', GeoPopup);

  // Dropdown of <store-selector>: a button that opens a listbox, optionally filtered by a search box.
  // Focus stays on the search box (or the list) and the active option is tracked with aria-activedescendant.
  class StoreSelectorListbox {
    constructor(root, { onSelect, onFilter }) {
      this.root = root;
      this.button = root.querySelector('[data-ss-button]');
      this.value = root.querySelector('[data-ss-value]');
      this.panel = root.querySelector('[data-ss-panel]');
      this.list = root.querySelector('[role="listbox"]');
      this.search = root.querySelector('[data-ss-search]');
      this.empty = root.querySelector('[data-ss-empty]');
      this.focusTarget = this.search || this.list;
      this.onSelect = onSelect;
      this.onFilter = onFilter;
      this.items = [];
      this.active = -1;

      this.button.addEventListener('click', () => (this.isOpen() ? this.close(true) : this.open()));
      this.button.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.open();
        }
      });
      this.focusTarget.addEventListener('keydown', this.onKeydown.bind(this));
      if (this.search) this.search.addEventListener('input', () => this.onFilter(normalize(this.search.value)));
      // mousedown keeps the focus in the search box while an option is picked.
      this.list.addEventListener('mousedown', (event) => event.preventDefault());
      this.list.addEventListener('click', (event) => {
        const option = event.target.closest('[role="option"]');
        if (option) this.pick(Number(option.dataset.index));
      });
      document.addEventListener('click', (event) => {
        if (this.isOpen() && !this.root.contains(event.target)) this.close(false);
      });
    }

    isOpen() {
      return !this.panel.hidden;
    }

    setItems(items, selectedValue) {
      this.items = items;
      const listId = this.list.id;
      this.list.innerHTML = items
        .map(
          (item, index) =>
            `<li id="${listId}-${index}" role="option" class="ss__option" data-index="${index}" aria-selected="${item.value === selectedValue}">${escapeHtml(item.label)}</li>`
        )
        .join('');
      if (this.empty) this.empty.hidden = items.length > 0;
      const selectedIndex = items.findIndex((item) => item.value === selectedValue);
      this.setActive(selectedIndex === -1 ? 0 : selectedIndex);
    }

    setActive(index) {
      const options = this.list.children;
      if (!options.length) {
        this.active = -1;
        this.focusTarget.removeAttribute('aria-activedescendant');
        return;
      }
      this.active = Math.max(0, Math.min(index, options.length - 1));
      Array.from(options).forEach((option, i) => option.classList.toggle('is-active', i === this.active));
      const activeOption = options[this.active];
      this.focusTarget.setAttribute('aria-activedescendant', activeOption.id);
      if (this.isOpen()) activeOption.scrollIntoView({ block: 'nearest' });
    }

    open() {
      this.panel.hidden = false;
      this.root.classList.add('is-open');
      this.button.setAttribute('aria-expanded', 'true');
      if (this.search) {
        this.search.value = '';
        this.onFilter('');
      }
      this.focusTarget.focus();
      this.setActive(this.active);
    }

    close(focusButton) {
      this.panel.hidden = true;
      this.root.classList.remove('is-open');
      this.button.setAttribute('aria-expanded', 'false');
      if (focusButton) this.button.focus();
    }

    pick(index) {
      const item = this.items[index];
      if (!item) return;
      this.close(true);
      this.onSelect(item.value);
    }

    onKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.setActive(this.active + 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.setActive(this.active - 1);
          break;
        case 'Home':
          if (this.search) return;
          event.preventDefault();
          this.setActive(0);
          break;
        case 'End':
          if (this.search) return;
          event.preventDefault();
          this.setActive(this.items.length - 1);
          break;
        case 'Enter':
          event.preventDefault();
          this.pick(this.active);
          break;
        case 'Escape':
          event.preventDefault();
          this.close(true);
          break;
        case 'Tab':
          this.close(false);
          break;
      }
    }
  }

  /*
   * <store-selector> (sections/main-store-selector.liquid): pick a country (and a language when the
   * country has more than one), then go to the man / woman collection of that country's store.
   * The collection links are real hrefs; for a country of this store the click submits the
   * localization form instead, with return_to = the collection page.
   */
  class StoreSelector extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.locale = document.documentElement.lang || 'en';
      this.current = this.dataset.current;
      this.currentLanguage = this.dataset.currentLanguage;
      this.ownCountries = this.readJson('[data-ss-countries]') || [];
      this.form = this.querySelector('[data-ss-form]');
      this.languageField = this.querySelector('[data-ss-language-field]');
      this.collectionLinks = Array.from(this.querySelectorAll('[data-ss-collection]'));
      this.userPicked = false;

      this.country = new StoreSelectorListbox(this.querySelector('[data-ss-country-field]'), {
        onSelect: (iso) => {
          this.userPicked = true;
          this.selectCountry(iso);
        },
        onFilter: (query) => this.renderCountries(query),
      });
      this.language = new StoreSelectorListbox(this.languageField, {
        onSelect: (code) => this.selectLanguage(code),
        onFilter: () => {},
      });
      this.collectionLinks.forEach((link) => link.addEventListener('click', this.onCollectionClick.bind(this)));

      const ready = loadStores(this.dataset.storesUrl)
        .catch((error) => {
          console.warn('[store-selector] Could not load the stores directory', error);
          return {};
        })
        .then((directory) => {
          const { entries, byIso, ownCollections } = safeBuildDirectory({
            ownCountries: this.ownCountries,
            directory,
            shopDomain: this.dataset.shopDomain,
            locale: this.locale,
          });
          this.entries = entries;
          this.byIso = byIso;
          this.ownCollections = ownCollections;
          this.selectCountry(byIso[this.current] ? this.current : entries[0] && entries[0].iso);
          this.classList.add('is-ready');
        });

      // Preselect the visitor's country (Shopify geolocation) unless they already picked one.
      Promise.all([ready, loadDetectedCountry(this.dataset.suggestionsUrl)]).then(([, detected]) => {
        const iso = String(detected || '').toUpperCase();
        if (iso && !this.userPicked && this.byIso[iso]) this.selectCountry(iso);
      });
    }

    readJson(selector) {
      const node = this.querySelector(selector);
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        return null;
      }
    }

    renderCountries(query) {
      const entries = query ? searchEntries(this.entries, query) : this.entries;
      this.country.setItems(
        entries.map((entry) => ({ value: entry.iso, label: entry.name })),
        this.selected && this.selected.iso
      );
    }

    languagesOf(entry) {
      return entry.own ? entry.languages : entry.links;
    }

    selectCountry(iso) {
      const entry = this.byIso && this.byIso[iso];
      if (!entry) return;
      this.selected = entry;
      this.country.value.textContent = entry.name;
      this.renderCountries('');

      const languages = this.languagesOf(entry);
      this.languageField.hidden = languages.length < 2;
      this.language.setItems(
        languages.map((language) => ({ value: language.code, label: language.label })),
        null
      );
      this.selectLanguage(this.defaultLanguage(entry, languages));
    }

    // This store's countries keep the current language when they have it; other stores follow the browser.
    defaultLanguage(entry, languages) {
      if (!languages.length) return null;
      const codes = languages.map((language) => language.code);
      const preferred = entry.own
        ? [this.currentLanguage]
        : (navigator.languages || [navigator.language]).map((code) => String(code || '').toLowerCase());
      for (const code of preferred) {
        if (!code) continue;
        const match = codes.find((candidate) => candidate === code || candidate === code.split('-')[0]);
        if (match) return match;
      }
      return codes[0];
    }

    selectLanguage(code) {
      const languages = this.languagesOf(this.selected);
      this.selectedLanguage = languages.find((language) => language.code === code) || languages[0] || null;
      if (this.selectedLanguage) {
        this.language.value.textContent = this.selectedLanguage.label;
        this.language.setItems(
          languages.map((language) => ({ value: language.code, label: language.label })),
          this.selectedLanguage.code
        );
      }
      this.updateLinks();
    }

    // Path of a collection in this store, without the locale prefix. A link set on the block wins over the directory.
    ownCollectionPath(link) {
      const custom = link.dataset.ssOwnPath;
      if (custom) return /^https?:/.test(custom) ? custom : `/${custom.replace(/^\//, '')}`;
      const path = this.ownCollections[link.dataset.ssCollection];
      return path ? `/${path.replace(/^\//, '')}` : null;
    }

    // Each collection block has a key (man, woman, kids...) looked up in the selected store's collections;
    // a store without that key hides the link.
    updateLinks() {
      this.collectionLinks.forEach((link) => {
        let href = null;
        if (this.selected.own) {
          const path = this.ownCollectionPath(link);
          if (path) {
            const root = (this.dataset.rootUrl || '/').replace(/\/$/, '');
            href = /^https?:/.test(path) ? path : `${root}${path}`;
          }
          link.removeAttribute('hreflang');
        } else if (this.selectedLanguage) {
          href = this.selectedLanguage.collections[link.dataset.ssCollection] || null;
          link.hreflang = this.selectedLanguage.code;
        }
        link.hidden = !href;
        if (href) link.href = href;
      });
    }

    onCollectionClick(event) {
      if (!this.selected || !this.selected.own) return;
      const language = this.selectedLanguage ? this.selectedLanguage.code : this.currentLanguage;
      if (this.selected.iso === this.current && language === this.currentLanguage) return;
      const path = this.ownCollectionPath(event.currentTarget);
      if (!path || /^https?:/.test(path)) return;
      event.preventDefault();
      setFormField(this.form, 'country_code', this.selected.iso);
      setFormField(this.form, 'language_code', language);
      setFormField(this.form, 'return_to', path);
      this.classList.add('is-submitting');
      this.form.submit();
    }
  }
  customElements.define('store-selector', StoreSelector);
})();
